import { FilterControls } from './FilterControls'
import { formatHumanName } from '@medplum/core'
import { CreateThreadBtn } from './CreateThreadBtn'
import { MyThreads } from './MyThreads'
import { ThreadDetails } from './ThreadDetails'
import type { MessagesPageStateController } from '../useMessagesPageState'
import { ActionIcon } from '@mantine/core'
import { IconX } from '@tabler/icons-react'

export const MessagesPageMinimizedView = ({
  controller,
  focusThreadId,
  onClose,
}: {
  controller: MessagesPageStateController
  focusThreadId?: string | null
  onClose?: () => void
}) => {
  const {
    preselectedPatient,
    threadsFilterOptions,
    handleThreadCreated,
    threadListRefreshKey,
    selectedThread,
    threadStatusMap,
    setThreadStatusMap,
    setSelectedThread,
    handleThreadRead,
    handleThreadUpdated,
    setThreadsFilterOptions,
  } = controller

  // Rides on the Active/Archived tab row instead of a band of its own: the
  // drawer is one viewport tall and every row it does not spend on chrome is a
  // row of conversation.
  const headerActions = (
    <>
      {onClose && (
        <ActionIcon
          variant="transparent"
          color="blue"
          radius="xl"
          size="lg"
          aria-label="Close messages"
          className="shrink-0"
          onClick={onClose}
        >
          <IconX size={20} />
        </ActionIcon>
      )}
      {threadsFilterOptions.selectedPatient && (
        // `min-w-0` so this is the piece that gives way when the row is tight;
        // a flex item will not shrink below its content without it.
        <div className="min-w-0 truncate text-xs text-slate-500">
          Filter by patient: {formatHumanName(threadsFilterOptions.selectedPatient.name?.[0])}
        </div>
      )}
      <FilterControls
        threadsFilterOptions={threadsFilterOptions}
        onThreadsFilterOptionsChange={setThreadsFilterOptions}
      />
      <CreateThreadBtn onCreated={handleThreadCreated} preselectedPatient={preselectedPatient} />
    </>
  )

  return (
    <div className="flex h-full flex-col bg-white p-4">
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        {/*
          Shrinkable, NOT `shrink-0`: as `shrink-0` this box took its full
          content height, and seventeen active threads pushed the selected
          conversation out of the panel. Its own height is bounded by the
          list's scroller (`MyThreads`, minimized), so whatever is left over
          goes to the conversation below.
        */}
        <div className="min-h-0 shrink overflow-hidden">
          <MyThreads
            refreshKey={threadListRefreshKey}
            selectedThreadId={selectedThread?.thread.id}
            threadStatusMap={threadStatusMap}
            onThreadStatusMapChange={setThreadStatusMap}
            onSelectThread={setSelectedThread}
            focusThreadId={focusThreadId}
            threadsFilterOptions={threadsFilterOptions}
            minimized
            headerActions={headerActions}
          />
        </div>
        <div className="mb-40px min-h-0 flex-1 overflow-hidden">
          <ThreadDetails
            threadSummary={selectedThread}
            onThreadRead={handleThreadRead}
            onThreadUpdated={handleThreadUpdated}
            minimized
          />
        </div>
      </div>
    </div>
  )
}
